import { EntityPhotosComponent } from '@shared/components/entity-photos/entity-photos.component';
import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { OrganizationLevel, OrganizationUnit, UnitEvent } from '../models/unit.model';
import { Router, RouterLink } from '@angular/router';
import { getOrganizationUnitIcon } from '@shared/utils/organization-unit-icon.util';
import { UnitService } from '../services/unit.service';
import { NotificationService } from '@shared/services/notification.service';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { DeleteConfirmDialogComponent, DeleteConfirmDialogData } from '@features/user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { ChangeParentDialogComponent, ChangeParentDialogData, ChangeParentDialogResult } from './change-parent-dialog.component';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-unit-header',
  standalone: true,
  imports: [EntityPhotosComponent, CommonModule, MatIconModule, MatButtonModule, MatMenuModule, MatTooltipModule, MatDialogModule, MatDividerModule, RouterLink],
  templateUrl: './unit-header.component.html',
  styleUrl: './unit-header.component.scss'
})
export class UnitHeaderComponent implements OnInit {
  @Input() unit!: OrganizationUnit;
  @Input() events: UnitEvent[] = [];

  private readonly userFullInfoService = inject(UserFullInfoService);

  get unitIcon(): string {
    return getOrganizationUnitIcon(this.unit.organizationLevelId);
  }

  get unitCode(): string | null {
    const code = this.unit.shortName?.trim();
    return code && code.toLocaleLowerCase('es-AR') !== this.unit.name.trim().toLocaleLowerCase('es-AR') ? code : null;
  }

  get parentHierarchy(): { id?: string; name: string; label: string; icon: string }[] {
    return [
      { id: this.unit.districtId, name: this.unit.districtName ?? '', label: 'Distrito', icon: 'workspaces' },
      { id: this.unit.areaId, name: this.unit.areaName ?? '', label: 'Área de Apóstol', icon: 'hive' },
      { id: this.unit.regionalChurchId, name: this.unit.regionalChurchName ?? '', label: 'Iglesia regional', icon: 'south_america' },
    ].filter(parent => !!parent.name && (parent.id ? parent.id !== this.unit.id : parent.name !== this.unit.name));
  }

  get inauguracionDate(): string | null {
    const ev = this.events.find(e => this.isInaugurationEvent(e));
    return ev?.eventDate ?? null;
  }

  private isInaugurationEvent(event: UnitEvent): boolean {
    const name = event.eventTypeName.toLocaleLowerCase('es-AR');
    return name.includes('inaugur');
  }

  get inauguracionAge(): string | null {
    const date = this.inauguracionDate;
    if (!date) return null;
    const [year, month, day] = date.split('-').map(Number);
    const today = new Date();
    let years = today.getFullYear() - year;
    if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) {
      years--;
    }
    return `${years} año${years !== 1 ? 's' : ''}`;
  }

  get isComunidad(): boolean {
    return this.unit.organizationLevelId === OrganizationLevel.Comunidad;
  }

  canManageUnit(): boolean {
    return this.userFullInfoService.canManageMemberDataSnapshot();
  }

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private unitService: UnitService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    if (!this.unit) {
      throw new Error('Unit input is required');
    }
  }

  getUnitPhoto(): string {
    return this.unit.photo || '';
  }

  showPhoto(): boolean {
    return this.unit.levelName === 'Comunidad';
  }

  onNavigateToEvents(): void {
    if (!this.canManageUnit()) {
      return;
    }

    this.router.navigate(['/units', this.unit.id, 'info']);
  }

  onAddMember(): void {
    if (!this.canManageUnit()) {
      return;
    }

    this.router.navigate(['/people/new'], { queryParams: { unitId: this.unit.id } });
  }

  onNavigateToParentHistory(): void {
    if (!this.canManageUnit()) {
      return;
    }

    this.router.navigate(['/units', this.unit.id, 'parent-history']);
  }

  navigateToUnit(unitId: string): void {
    this.router.navigate(['/units', unitId]);
  }

  navigateToParentUnit(): void {
    if (this.unit.parentUnitId) {
      this.router.navigate(['/units', this.unit.parentUnitId]);
    }
  }

  onMenuAction(action: string): void {
    if (!this.canManageUnit()) {
      return;
    }

    switch (action) {
      case 'edit':
        this.router.navigate(['/units', this.unit.id, 'edit']);
        break;
      case 'change-parent':
        this.openChangeParentDialog();
        break;
      case 'settings':
        console.log(`Settings for unit ${this.unit.id}`);
        break;
      case 'delete':
        this.openDeleteDialog();
        break;
    }
  }

  openChangeParentDialog(): void {
    const dialogRef = this.dialog.open(ChangeParentDialogComponent, modalDialogConfig({
      data: {
        unitId: this.unit.id,
        unitName: this.unit.name,
        currentParentId: this.unit.parentUnitId ?? undefined,
        currentParentName: this.unit.districtName ?? this.unit.areaName ?? undefined,
      } as ChangeParentDialogData,
      size: 'lg',
      layout: 'form',
    }));

    dialogRef.afterClosed().pipe(
      filter((result): result is ChangeParentDialogResult => result !== null && result !== undefined)
    ).subscribe(result => {
      this.unitService.changeParent(this.unit.id, {
        newParentUnitId: result.newParentUnitId,
        isCorrection: result.isCorrection,
        reason: result.reason,
        effectiveDate: result.effectiveDate,
      }).subscribe({
        next: () => {
          this.notificationService.success('Unidad superior actualizada correctamente.');
          this.router.navigate(['/units', this.unit.id]);
        },
        error: (err) => {
          const message = err?.error?.detail || err?.error?.title || 'No se pudo actualizar la unidad superior.';
          this.notificationService.error(message);
        }
      });
    });
  }

  openDeleteDialog(): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar unidad',
        message: `¿Estás seguro de que querés eliminar <strong>${this.unit.name}</strong>? Esta acción no se puede deshacer.`,
        confirmButtonText: 'Eliminar',
        requiresReason: true,
      } as DeleteConfirmDialogData,
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed().pipe(
      filter(result => result?.confirmed === true)
    ).subscribe(result => {
      const reason: string = result.deletionReason;
      this.unitService.deleteUnit(this.unit.id, reason).subscribe({
        next: () => {
          this.notificationService.success(`La unidad "${this.unit.name}" fue eliminada correctamente.`);
          this.router.navigate(['/units']);
        },
        error: (err) => {
          const message = err?.detail || err?.message || 'No se pudo eliminar la unidad.';
          this.notificationService.error(message);
        }
      });
    });
  }
}
